import React, { useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import ThirdPersonWalker from "../avatar/ThirdPersonWalker";
import { DEFAULT_CAM_POS, DEFAULT_CAM_TARGET } from "../constants";

export default function Controls({
  mode,
  controlsRef,
  initialCam,
  initialTarget,
  avatarUrl,
  isRenderableInScene,
  joystickInputRef,
  teleportRef,
}) {
  const { camera } = useThree();
  const initialApplied = useRef(false);

  // While the headset is actively tracking the scene, hand off to it.
  const xrSession = useXR((s) => s.session);
  const inXR = !!xrSession;

  useEffect(() => {
    if (inXR) return; // XR owns the camera
    if (mode === "walk") {
      initialApplied.current = true;
      return;
    }
    if (!initialApplied.current && initialCam) {
      camera.position.set(initialCam[0], initialCam[1], initialCam[2]);
    } else {
      camera.position.set(...DEFAULT_CAM_POS);
    }
    initialApplied.current = true;
  }, [mode, camera, initialCam, inXR]);

  useEffect(() => {
    if (inXR) return;
    if (
      mode === "orbit" &&
      initialTarget &&
      controlsRef &&
      controlsRef.current &&
      controlsRef.current.target
    ) {
      controlsRef.current.target.set(
        initialTarget[0],
        initialTarget[1],
        initialTarget[2]
      );
      controlsRef.current.update && controlsRef.current.update();
    }
  }, [mode, controlsRef, initialTarget, inXR]);

  // Hide HUD/web-controls while in an XR session — the headset is in charge.
  if (inXR) return null;

  if (mode === "walk") {
    return (
      <ThirdPersonWalker
        enabled
        avatarUrl={avatarUrl}
        isRenderableInScene={isRenderableInScene}
        inputRef={joystickInputRef}
        teleportRef={teleportRef}
      />
    );
  }

  return (
    <OrbitControls
      ref={controlsRef}
      enablePan
      enableZoom
      enableDamping
      dampingFactor={0.08}
      maxPolarAngle={Math.PI / 2.1}
      minDistance={8}
      maxDistance={90}
      target={DEFAULT_CAM_TARGET}
      touches={{ ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN }}
      makeDefault
    />
  );
}
